import { useEffect, useState, useRef, useMemo } from 'react';
import { TokenConfigType } from '@/constants/types/currency';
import { pinnedTokens } from '@/constants/project-specific/tokens';
import { RequiredTokenListType } from '@/constants/project-specific';
import { fallbackChainId } from '@/constants/chain-specific';
import { formatPrice } from '@/util/portfolio/formatPrice';
import { motion, useAnimation } from 'motion/react';
import { debounce } from 'lodash';
import { TokenStats } from '@/lib/price-data/getTokenStats';
import TokenStatsPopover from '@/components/Tokens/TokenStatsPopover';
export type TickerDisplayOption = 'price' | 'priceChange' | 'volume' | 'none';
export type TickerSortOption = 'none' | 'priceChange' | 'volume' | 'price';
interface PriceTickerProps {
  chainId: RequiredTokenListType;
  tokenPrices?: Record<string, number>;
  tokenStats?: Record<string, TokenStats>;
  onTokenSelect?: (token: TokenConfigType) => void;
  // Customization options
  speed?: number; // Duration in seconds for one complete cycle
  displayOptions?: TickerDisplayOption[];
  sortBy?: TickerSortOption;
  pauseOnHover?: boolean;
  className?: string;
}
const PriceTicker = ({
  chainId,
  tokenPrices,
  tokenStats,
  onTokenSelect,
  speed = 40,
  displayOptions = ['price', 'priceChange'],
  sortBy = 'volume',
  pauseOnHover = true,
  className
}: PriceTickerProps) => {
  const [tickerTokens, setTickerTokens] = useState<TokenConfigType[]>([]);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls1 = useAnimation();
  const controls2 = useAnimation();

  // Memoize token sorting to prevent unnecessary recalculations
  const sortedTokens = useMemo(() => {
    let availableTokens = pinnedTokens[chainId ?? fallbackChainId].display;
    if (sortBy !== 'none' && tokenStats) {
      availableTokens = [...availableTokens].sort((a, b) => {
        const aAddress = a.address.toLowerCase();
        const bAddress = b.address.toLowerCase();
        const aStats = tokenStats[aAddress];
        const bStats = tokenStats[bAddress];
        if (!aStats || !bStats) return 0;
        switch (sortBy) {
          case 'priceChange':
            return (bStats.priceChange.h24 ?? 0) - (aStats.priceChange.h24 ?? 0);
          case 'volume':
            return (bStats.volume.h24 ?? 0) - (aStats.volume.h24 ?? 0);
          case 'price':
            return (bStats.price ?? 0) - (aStats.price ?? 0);
          default:
            return 0;
        }
      });
    }
    return availableTokens;
  }, [chainId, tokenStats, sortBy]);
  useEffect(() => {
    setTickerTokens(sortedTokens);
  }, [sortedTokens]);
  useEffect(() => {
    if (tokenPrices && Object.keys(tokenPrices).length > 0) {
      setShouldAnimate(true);
    } else {
      setShouldAnimate(false);
    }
  }, [tokenPrices]);

  // Memoize the updateContentWidth function
  const updateContentWidth = useMemo(() => {
    return debounce(() => {
      if (!containerRef.current) return;
      const firstSet = containerRef.current.firstElementChild as HTMLElement;
      if (firstSet) {
        setContentWidth(firstSet.offsetWidth);
      }
    }, 250);
  }, []);
  useEffect(() => {
    window.addEventListener('resize', updateContentWidth);
    return () => window.removeEventListener('resize', updateContentWidth);
  }, [updateContentWidth]);
  useEffect(() => {
    updateContentWidth();
  }, [tickerTokens, tokenPrices, updateContentWidth]);

  // Memoize animation controls
  useEffect(() => {
    if (shouldAnimate && !isHovered) {
      const animationConfig = {
        x: -contentWidth,
        transition: {
          duration: speed,
          repeat: Infinity,
          ease: "linear" as const
        }
      };
      controls1.start(animationConfig);
      controls2.start(animationConfig);
    } else {
      controls1.stop();
      controls2.stop();
    }
  }, [shouldAnimate, isHovered, contentWidth, speed, controls1, controls2]);

  // Memoize token info rendering
  const renderTokenInfo = useMemo(() => (token: TokenConfigType) => {
    const tokenAddress = token.address.toLowerCase();
    const stats = tokenStats?.[tokenAddress];
    const price = tokenPrices?.[tokenAddress];
    if (!price) return <span className="text-sm text-gray-400">Loading...</span>;
    return <div className="flex items-center gap-2">
        {displayOptions.includes('price') && <span className="text-sm text-gray-400">
            ${formatPrice(Number(price) ?? 0)}
          </span>}
        {displayOptions.includes('priceChange') && stats?.priceChange?.h24 ? <span className={`text-sm ${stats.priceChange.h24 >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {stats.priceChange.h24 >= 0 ? '+' : ''}
            {stats.priceChange.h24.toFixed(2)}%
          </span> : ''}
        {displayOptions.includes('volume') && stats?.volume?.h24 && <span className="text-sm text-gray-400">
            Vol: ${formatPrice(stats.volume.h24)}
          </span>}
      </div>;
  }, [displayOptions, tokenPrices, tokenStats]);

  // Memoize token rendering
  const renderToken = useMemo(() => (token_0: TokenConfigType, index: number) => <TokenStatsPopover key={`${index}-${token_0.address}`} token={token_0} tokenStats={tokenStats?.[token_0.address.toLowerCase()]}>
      <div className="flex items-center gap-4 mx-8 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => onTokenSelect?.(token_0)}>
        <img src={token_0.logo} alt={token_0.name} className="w-6 h-6 rounded-full" loading="lazy" decoding="async" />
        <span className="text-sm font-medium">{token_0.symbol}</span>
        {renderTokenInfo(token_0)}
      </div>
    </TokenStatsPopover>, [onTokenSelect, renderTokenInfo, tokenStats]);
  return <div className={`w-full overflow-hidden bg-black/50 py-2 mx-auto z-10 ${className}`} onMouseEnter={() => pauseOnHover && setIsHovered(true)} onMouseLeave={() => pauseOnHover && setIsHovered(false)}>
      <div ref={containerRef} className="flex whitespace-nowrap">
        <motion.div className="flex whitespace-nowrap" animate={controls1} initial={{
        x: 0
      }}>
          {tickerTokens.map((token_1, index_0) => renderToken(token_1, index_0))}
        </motion.div>

        <motion.div className="flex whitespace-nowrap" animate={controls2} initial={{
        x: 0
      }}>
          {tickerTokens.map((token_2, index_1) => renderToken(token_2, index_1))}
        </motion.div>
      </div>
    </div>;
};
export default PriceTicker;